import { Tooltip } from 'antd';
import LinkButton from 'components/common/LinkButton/LinkButton';
import {
  INPUT_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  ANALYSIS_NO_DATA_MESSAGE,
  INPUT_DOC_TAB_DIAGNOSTICS,
  INPUT_DOC_TAB_PLANNING,
  LINK_OPEN_PLAN_LABEL,
  LINK_EFFICIENCY_TRENDS_LABEL,
  SUMMARY_ENTITY_LABELS,
} from 'constants/uiConstants';
import dayjs from 'dayjs';
import WorkspaceEmptyState from 'pages/FarmsPage/Workspace/WorkspaceEmptyState';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import React, { useMemo, useRef, useState } from 'react';
import {
  LuArrowDown,
  LuArrowUp,
  LuChevronDown,
  LuChevronRight,
  LuInfo,
} from 'react-icons/lu';
import type {
  AnalysisColumnKey,
  AnalysisGroupBy,
  AnalysisSummaryMode,
  SummaryEntityCategory,
} from '../../../../redux/slices/InsightsSlice';
import {
  ANALYSIS_COST_CATEGORIES,
  resolveDistinctYieldUnits,
  resolveGroupKey,
  type AnalysisRow,
} from './computeAnalysis';

/**
 * Paddock, Crop, and Variety are the only columns whose values are
 * unpredictable-length free text, so they're the only ones worth letting a
 * user resize. Widths snap between these preset Tailwind classes (rather
 * than following the cursor to an arbitrary pixel) since Tailwind's static
 * build can't generate a class for a width it never sees written literally
 * in source — these strings need to stay literal for that reason.
 */
type ResizableColumnKey = 'paddockName' | 'crop' | 'variety';
// 16px per step (Tailwind's spacing scale is a flat 4px per unit up to w-64)
// rather than the coarser 32px jumps of skipping every other class — finer
// steps make the snap-as-you-drag feel closer to a continuous resize.
const COLUMN_WIDTH_STEPS: { className: string; px: number }[] = [
  { className: 'w-28', px: 112 },
  { className: 'w-32', px: 128 },
  { className: 'w-36', px: 144 },
  { className: 'w-40', px: 160 },
  { className: 'w-44', px: 176 },
  { className: 'w-48', px: 192 },
  { className: 'w-52', px: 208 },
  { className: 'w-56', px: 224 },
  { className: 'w-60', px: 240 },
  { className: 'w-64', px: 256 },
  { className: 'w-72', px: 288 },
  { className: 'w-80', px: 320 },
  { className: 'w-96', px: 384 },
];
const DEFAULT_COLUMN_WIDTH_INDEX: Record<ResizableColumnKey, number> = {
  paddockName: 9, // w-64 (256px) — matches the column's previous fixed max-width
  crop: 3, // w-40 (160px) — matches the column's previous fixed max-width
  variety: 3,
};

const nearestWidthIndex = (targetPx: number): number =>
  COLUMN_WIDTH_STEPS.reduce(
    (nearest, step, i) =>
      Math.abs(step.px - targetPx) <
      Math.abs(COLUMN_WIDTH_STEPS[nearest].px - targetPx)
        ? i
        : nearest,
    0
  );

/** Drag-to-resize handle for a column header — snaps to the nearest COLUMN_WIDTH_STEPS entry as the cursor moves, rather than following it exactly (see ResizableColumnKey comment). */
const ResizeHandle: React.FC<{
  widthIndex: number;
  onResize: (index: number) => void;
}> = ({ widthIndex, onResize }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startPx = COLUMN_WIDTH_STEPS[widthIndex].px;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      onResize(nearestWidthIndex(startPx + (moveEvent.clientX - startX)));
    };
    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <span
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize column"
      // The hit target (w-3) is wider than the visible indicator (w-px) so
      // it's easy to grab without needing a permanently thick line — the
      // line itself stays faint at rest and only thickens/darkens on
      // hover/drag, which is what actually signals "this can be resized".
      className="group absolute right-0 top-0 flex h-full w-3 cursor-col-resize select-none justify-center"
    >
      <span className="my-1.5 w-px bg-gray-argent transition-all group-hover:w-1 group-hover:bg-charcoal" />
    </span>
  );
};

interface AnalysisGridProps {
  rows: AnalysisRow[];
  showPerHa: boolean;
  hiddenColumns: AnalysisColumnKey[];
  summaryMode: AnalysisSummaryMode;
  weightAverageByHa: boolean;
  groupBy: AnalysisGroupBy;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
}

type SortKey = 'paddockName' | AnalysisColumnKey;

type SortDirection = 'asc' | 'desc';

const fmt2 = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatCurrency = (value: number): string => {
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  return `${value < 0 ? '-' : ''}$${formatted}`;
};

const formatPercent = (value: number): string => `${Math.round(value * 100)}%`;

/** Matches the "MMMM DD, YYYY" convention used for date columns elsewhere (e.g. Tasks grid). */
const formatStartDate = (value: string | null): string => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format('MMMM DD, YYYY') : '—';
};

// Tooltips for columns whose value is calculated/derived rather than a raw
// stored attribute — identifiers (Paddock, Farm Name, Crop, Variety, Start
// Date) and ha don't get one, since there's nothing to explain.
const TOOLTIP_PRICE =
  "Area-weighted average price across this paddock's production steps, at the selected Price scenario.";
const TOOLTIP_YIELD =
  "Area-weighted average yield across this paddock's production steps, at the selected Production scenario.";
const TOOLTIP_PRODUCTION = 'Yield × Area — total production at the selected scenario.';
const TOOLTIP_INCOME =
  'Yield × Price × Area — revenue at the selected Price/Production scenario.';
const TOOLTIP_TOTAL_COSTS =
  'Sum of all cost categories (Seed, Soil Input, Plant Protection, Seed Dress, Biostimulant, Operation) for this paddock.';
const TOOLTIP_GROSS_MARGIN = 'Income minus Total Costs, at the selected scenario.';
const TOOLTIP_COST_PERCENT = 'Total Costs ÷ Income, expressed as a percentage.';
const costCategoryTooltip = (label: string): string =>
  `Total ${label} cost: rate × cost × area, summed across all matching steps in this paddock's plan.`;

/** A muted "—" with a tooltip explaining why the value can't be shown. */
const Dash: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <Tooltip title={tooltip}>
    <span className="cursor-help font-normal text-gray-muted">—</span>
  </Tooltip>
);

/** Small inline info icon for a header — explains a mixed-unit header on the spot, not just at the footer. */
const HeaderInfo: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <Tooltip title={tooltip}>
    <span className="ml-1 inline-flex cursor-help">
      <LuInfo size={13} className="text-gray-argent" />
    </span>
  </Tooltip>
);

/** Caption-style treatment for a unit label (e.g. "t/ha") — small and muted like a caption, but bold enough to stay legible at that size. */
const UnitCaption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="ml-1 text-xs font-semibold text-gray-muted">{children}</span>
);

const sum = (values: number[]): number => values.reduce((a, b) => a + b, 0);
const mean = (values: number[]): number =>
  values.length > 0 ? sum(values) / values.length : 0;

/**
 * Sum-of-totals in Total mode ($); area-weighted total ÷ ha in Total mode
 * ($/ha) or an Average explicitly weighted by ha — both reduce to the same
 * total-$-over-total-ha figure; otherwise the mean of the displayed values in
 * Average mode.
 */
const aggregateMoney = (
  rows: AnalysisRow[],
  totals: number[],
  perHaValues: number[],
  showPerHa: boolean,
  summaryMode: AnalysisSummaryMode,
  weightAverageByHa: boolean
): number => {
  if (showPerHa && (summaryMode === 'total' || weightAverageByHa)) {
    const totalArea = sum(rows.map((r) => r.areaHa));
    return totalArea > 0 ? sum(totals) / totalArea : 0;
  }
  if (summaryMode === 'total') return sum(totals);
  return mean(showPerHa ? perHaValues : totals);
};

/** Resolves the value a column sorts by — respects the $/ha vs $ display toggle for money columns. */
const getSortValue = (
  row: AnalysisRow,
  key: SortKey,
  showPerHa: boolean
): number | string => {
  switch (key) {
    case 'paddockName':
      return row.paddockName;
    case 'farmName':
      return row.farmName;
    case 'areaHa':
      return row.areaHa;
    case 'crop':
      return row.crop;
    case 'variety':
      return row.variety;
    case 'startDate':
      return row.startDate ? new Date(row.startDate).getTime() : -Infinity;
    case 'price':
      return row.price;
    case 'yield':
      return row.yield;
    case 'production':
      return row.production;
    case 'income':
      return showPerHa ? row.incomePerHa : row.incomeTotal;
    case 'totalCosts':
      return showPerHa ? row.totalCostsPerHa : row.totalCostsTotal;
    case 'grossMargin':
      return showPerHa ? row.grossMarginPerHa : row.grossMarginTotal;
    case 'costPercentIncome':
      return row.costPercentIncome ?? -Infinity;
    default:
      return (showPerHa ? row.costsPerHa : row.costsTotal)[key] ?? 0;
  }
};

/** Sorts a row subset by the active column — pure, so the flat list and each group can reuse it independently without re-sorting each other. */
const sortRowsBy = (
  rowsToSort: AnalysisRow[],
  key: SortKey | null,
  dir: SortDirection,
  showPerHa: boolean
): AnalysisRow[] => {
  if (!key) return rowsToSort;
  const mult = dir === 'asc' ? 1 : -1;
  return [...rowsToSort].sort((a, b) => {
    const av = getSortValue(a, key, showPerHa);
    const bv = getSortValue(b, key, showPerHa);
    if (typeof av === 'string' && typeof bv === 'string') {
      return mult * av.localeCompare(bv, undefined, { sensitivity: 'base' });
    }
    return mult * ((av as number) - (bv as number));
  });
};

/** Plain aggregate values for a row subset. The grand-total footer and each group's subtotal row both call this — same math, different row subset. Exported for AnalysisView's copy-to-clipboard, so the group subtotals it writes out match what's on screen exactly. */
export interface RowAggregates {
  area: number;
  meanPrice: number;
  meanYield: number;
  production: number;
  income: number;
  costsByCategory: Partial<Record<SummaryEntityCategory, number>>;
  totalCosts: number;
  grossMargin: number;
  costPercent: number | null;
}

export const aggregateRowSet = (
  rowsSubset: AnalysisRow[],
  showPerHa: boolean,
  summaryMode: AnalysisSummaryMode,
  weightAverageByHa: boolean
): RowAggregates => {
  const isAvg = summaryMode === 'average';
  const incomeValues = rowsSubset.map((r) => r.incomeTotal);
  const incomePerHaValues = rowsSubset.map((r) => r.incomePerHa);
  const totalCostsValues = rowsSubset.map((r) => r.totalCostsTotal);
  const totalCostsPerHaValues = rowsSubset.map((r) => r.totalCostsPerHa);
  const gmValues = rowsSubset.map((r) => r.grossMarginTotal);
  const gmPerHaValues = rowsSubset.map((r) => r.grossMarginPerHa);

  const costsByCategory: Partial<Record<SummaryEntityCategory, number>> = {};
  for (const category of ANALYSIS_COST_CATEGORIES) {
    const totals = rowsSubset.map((r) => r.costsTotal[category] ?? 0);
    const perHaValues = rowsSubset.map((r) => r.costsPerHa[category] ?? 0);
    costsByCategory[category] = aggregateMoney(
      rowsSubset,
      totals,
      perHaValues,
      showPerHa,
      summaryMode,
      weightAverageByHa
    );
  }

  const costPercent =
    summaryMode === 'total' || weightAverageByHa
      ? sum(incomeValues) > 0
        ? sum(totalCostsValues) / sum(incomeValues)
        : null
      : (() => {
          const nonNull = rowsSubset
            .map((r) => r.costPercentIncome)
            .filter((v): v is number => v !== null);
          return nonNull.length > 0 ? mean(nonNull) : null;
        })();

  return {
    area: sum(rowsSubset.map((r) => r.areaHa)),
    meanPrice: mean(rowsSubset.map((r) => r.price)),
    meanYield: mean(rowsSubset.map((r) => r.yield)),
    production: isAvg
      ? mean(rowsSubset.map((r) => r.production))
      : sum(rowsSubset.map((r) => r.production)),
    income: aggregateMoney(
      rowsSubset,
      incomeValues,
      incomePerHaValues,
      showPerHa,
      summaryMode,
      weightAverageByHa
    ),
    costsByCategory,
    totalCosts: aggregateMoney(
      rowsSubset,
      totalCostsValues,
      totalCostsPerHaValues,
      showPerHa,
      summaryMode,
      weightAverageByHa
    ),
    grossMargin: aggregateMoney(
      rowsSubset,
      gmValues,
      gmPerHaValues,
      showPerHa,
      summaryMode,
      weightAverageByHa
    ),
    costPercent,
  };
};

// The sticky cell must match its row's own alternating stripe or it
// visually breaks the pattern once content scrolls behind it.
const stickyCol0BodyClass = (rowBg: string): string =>
  `sticky left-0 ${rowBg} group-hover:bg-rowHover`;
// border-b-2 matches the footer's border-t-2 weight, framing the body rows
// between a clearly separated header and totals row. text-sm (smaller than
// the table's default text-base) keeps column labels from competing with
// the actual data for visual weight.
const HEADER_BASE =
  'pb-2 text-sm font-bold text-charcoal sticky top-0 bg-white border-b-2 border-gray-argent';
// border-t-2 (rather than the 1px border used elsewhere) so the Total/Average
// row reads as a clearly-separated summary line, not just another row.
const FOOTER_BASE =
  'py-1.5 font-bold tabular-nums sticky bottom-0 bg-white border-t-2 border-gray-argent';
// Row-divider borders live on each <td>, not the <tr> — with the table's
// border-separate model (needed for the header/footer borders above to
// render reliably on sticky cells), borders set on <tr> itself are ignored
// entirely per the CSS separated-borders model.
const ROW_DIVIDER = 'border-b border-gray-smoke';
const GROUP_ROW_DIVIDER = 'border-b border-gray-argent';

const headerCellClass = `${HEADER_BASE} z-10 w-24 px-2 text-right`;
const headerTextBaseClass = `${HEADER_BASE} z-10 pr-6 text-left`;
const headerTextClass = `${headerTextBaseClass} max-w-[10rem]`;
// Crop/Variety headers add their own resize handle, so they need `relative`
// to position it and their own width class instead of the shared max-width.
const headerResizableTextClass = `${headerTextBaseClass} relative`;
const bodyCellClass = `w-24 px-2 py-1.5 text-right tabular-nums text-charcoal ${ROW_DIVIDER}`;
const footerCellClass = `${FOOTER_BASE} z-10 w-24 px-2 text-right text-charcoal`;
// Subtotal cells (group header row) — semibold, one step down from the grand
// total's full bold, so the two read as a clear hierarchy.
const groupCellClass = `w-24 px-2 py-1.5 text-right tabular-nums font-semibold text-charcoal ${GROUP_ROW_DIVIDER}`;

// Total Costs sums the individual cost-category columns to its left — pl-4
// (overriding the cell's own px-2) opens a small gap so it doesn't read as
// just another itemized category, without a rule (a vertical line here read
// as noise, same call as the Area/Crop gutter below).
const TOTAL_COSTS_GUTTER = 'pl-4';

// Area is a right-aligned number immediately followed by a left-aligned
// text column (Crop) with no padding of its own — without a gutter the
// digits sit flush against the crop name. pr-4 overrides the cell's own
// px-2 to open up that gap (no rule — a vertical line here read as noise).
const AREA_GUTTER = 'pr-4';

// The row's own border is a no-op (border-separate ignores <tr> borders) —
// GROUP_ROW_DIVIDER is applied per-cell instead, including the sticky one.
// No collapsed/expanded fill distinction — the chevron already says which
// state a group is in, and the bold text/row-count/divider already set it
// apart from paddock rows, so a background fill on top was redundant and
// collided visually with the hover color.
const GROUP_HEADER_ROW = 'group cursor-pointer hover:bg-rowHover';
const GROUP_HEADER_STICKY_BASE = 'sticky left-0 bg-white group-hover:bg-rowHover';

/** Sort-state icon — decorative only; the actual state is conveyed to assistive tech via aria-sort on the <th>. */
const SortArrow: React.FC<{ active: boolean; direction: SortDirection }> = ({
  active,
  direction,
}) => {
  const Icon = direction === 'asc' ? LuArrowUp : LuArrowDown;
  return (
    <Icon
      aria-hidden="true"
      size={15}
      strokeWidth={3.5}
      className={`ml-1 shrink-0 ${active ? 'text-gray-dark' : 'text-gray-argent'}`}
    />
  );
};

/** WAI-ARIA sort state for a column header — 'none' marks it sortable-but-inactive rather than omitting the attribute. */
const getAriaSort = (
  sortKeyId: SortKey,
  activeSortKey: SortKey | null,
  sortDir: SortDirection
): 'ascending' | 'descending' | 'none' =>
  activeSortKey !== sortKeyId ? 'none' : sortDir === 'asc' ? 'ascending' : 'descending';

interface SortButtonProps {
  sortKeyId: SortKey;
  activeSortKey: SortKey | null;
  sortDir: SortDirection;
  onSort: (key: SortKey) => void;
  align: 'left' | 'right';
  /** Explains how a calculated column's value is derived — shown on hover instead of the plain "Click to sort" hint. */
  tooltip?: string;
  children: React.ReactNode;
}

const SortButton: React.FC<SortButtonProps> = ({
  sortKeyId,
  activeSortKey,
  sortDir,
  onSort,
  align,
  tooltip,
  children,
}) => {
  const isActive = activeSortKey === sortKeyId;
  // A dotted underline on the label (not the sort arrow) is the affordance
  // that a tooltip exists here — without it, a header with a tooltip looked
  // identical to one without, so there was no way to discover it short of
  // hovering blind. Matches RibbonRow's identical dotted-underline
  // convention for its own labelled tooltips.
  const label = tooltip ? (
    <span className="underline decoration-dotted decoration-gray-argent underline-offset-2">
      {children}
    </span>
  ) : (
    children
  );
  const button = (
    <button
      type="button"
      onClick={() => onSort(sortKeyId)}
      className={`inline-flex w-full cursor-pointer items-center border-none bg-transparent p-0 font-bold text-inherit hover:text-charcoal ${
        align === 'right' ? 'justify-end' : 'justify-start'
      }`}
      title={tooltip ? undefined : 'Click to sort'}
    >
      {label}
      <SortArrow active={isActive} direction={isActive ? sortDir : 'asc'} />
    </button>
  );
  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

const AnalysisGrid: React.FC<AnalysisGridProps> = ({
  rows,
  showPerHa,
  hiddenColumns,
  summaryMode,
  weightAverageByHa,
  groupBy,
  collapsedGroups,
  onToggleGroup,
}) => {
  const isAverage = summaryMode === 'average';
  const navigateToFarmsView = useFarmsLinkNavigate();
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  // Marks the sticky Paddock column's trailing edge only once the grid is
  // actually scrolled away from the start — at scrollLeft 0 there's nothing
  // scrolled beneath it yet, so a permanent rule there would just be noise.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const handleScroll = () =>
    setIsScrolled((scrollRef.current?.scrollLeft ?? 0) > 0);
  const stickyEdgeClass = isScrolled ? 'border-r border-gray-argent' : '';

  // Local, ephemeral — user-adjusted column widths reset with the rest of
  // the grid's display state rather than persisting across sessions.
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTH_INDEX);
  // The transition is what makes the step-snapping actually feel smooth —
  // without it, each step change is an instant jump; with it, the column
  // glides to the new width instead.
  const columnWidthClass = (column: ResizableColumnKey): string =>
    `${COLUMN_WIDTH_STEPS[columnWidths[column]].className} transition-[width] duration-150 ease-out`;
  const setColumnWidth = (column: ResizableColumnKey, index: number) =>
    setColumnWidths((prev) =>
      prev[column] === index ? prev : { ...prev, [column]: index }
    );

  const hidden = new Set(hiddenColumns);
  const showFarmName = !hidden.has('farmName');
  const showAreaHa = !hidden.has('areaHa');
  const showCrop = !hidden.has('crop');
  const showVariety = !hidden.has('variety');
  const showStartDateCol = !hidden.has('startDate');
  const showPriceCol = !hidden.has('price');
  const showYieldCol = !hidden.has('yield');
  const showProductionCol = !hidden.has('production');
  const showIncomeCol = !hidden.has('income');
  const showTotalCostsCol = !hidden.has('totalCosts');
  const showGrossMarginCol = !hidden.has('grossMargin');
  const showCostPercentCol = !hidden.has('costPercentIncome');
  const visibleCostCategories = ANALYSIS_COST_CATEGORIES.filter(
    (c) => !hidden.has(c)
  );
  // Separates the cost-breakdown detail columns from the bottom-line summary
  // columns — only meaningful when at least one cost category is actually
  // shown; otherwise it'd sit right after Income with nothing to divide.
  const totalCostsGutterClass =
    visibleCostCategories.length > 0 ? TOTAL_COSTS_GUTTER : '';

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedRows = useMemo(
    () => sortRowsBy(rows, sortKey, sortDir, showPerHa),
    [rows, sortKey, sortDir, showPerHa]
  );

  // Groups keep their own row order (sorted independently) rather than
  // sorting across the whole flat list — clicking a column header reorders
  // rows within each group, it never breaks the grouping apart.
  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map<string, AnalysisRow[]>();
    for (const row of rows) {
      const key = resolveGroupKey(row, groupBy);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(([key, groupRows]) => ({
        key,
        rows: sortRowsBy(groupRows, sortKey, sortDir, showPerHa),
      }));
  }, [rows, groupBy, sortKey, sortDir, showPerHa]);

  if (rows.length === 0) {
    return <WorkspaceEmptyState message={ANALYSIS_NO_DATA_MESSAGE} />;
  }

  // Grand-total aggregates — always computed from the full, ungrouped row
  // set, regardless of which groups are currently collapsed on screen.
  const totals = aggregateRowSet(rows, showPerHa, summaryMode, weightAverageByHa);

  // When every paddock in view records yield in the same unit, the unit is
  // stated once in the column header instead of repeating on every cell.
  // resolveDistinctYieldUnits compares case/whitespace-insensitively, so a
  // labelling inconsistency ("t" vs "T") doesn't count as a real difference
  // — shared with AnalysisView's copy-to-clipboard so both agree on this.
  // Genuinely mixed units (e.g. cotton in bales/ha alongside grain in
  // tonnes/ha) fall back to showing each row's own unit inline, and block
  // the footer sum/average rather than silently blending incompatible units.
  // This check is global (all rows in view), not per-group, for simplicity.
  const distinctYieldUnits = resolveDistinctYieldUnits(rows);
  const uniformYieldUnit =
    distinctYieldUnits.length === 1 ? distinctYieldUnits[0] : null;
  const yieldAmbiguous = !uniformYieldUnit;
  // Surfaced in the mixed-unit tooltip so the exact units in view are named
  // (rather than a generic "different units" message) — with many paddocks
  // in scope (e.g. Org level), scrolling to spot the one outlier by eye
  // isn't reliable.
  const distinctYieldUnitsLabel = distinctYieldUnits.join(', ');

  const renderPriceCell = (aggregates: RowAggregates): React.ReactNode =>
    !isAverage ? (
      <Dash tooltip="Price isn't summed across paddocks — switch Summary to Average to see a mean price." />
    ) : (
      formatCurrency(aggregates.meanPrice)
    );

  const renderYieldCell = (aggregates: RowAggregates): React.ReactNode =>
    !isAverage ? (
      <Dash tooltip="Yield isn't summed across paddocks — switch Summary to Average to see a mean yield." />
    ) : yieldAmbiguous ? (
      <Dash
        tooltip={`Paddocks in this view record yield in different units (${distinctYieldUnitsLabel}), so an average yield can't be calculated.`}
      />
    ) : (
      <>
        {fmt2(aggregates.meanYield)}
        <UnitCaption>{uniformYieldUnit}/ha</UnitCaption>
      </>
    );

  const renderProductionCell = (aggregates: RowAggregates): React.ReactNode =>
    yieldAmbiguous ? (
      <Dash
        tooltip={`Paddocks in this view record yield in different units (${distinctYieldUnitsLabel}), so a total/average production can't be calculated.`}
      />
    ) : (
      <>
        {fmt2(aggregates.production)}
        <UnitCaption>{uniformYieldUnit}</UnitCaption>
      </>
    );

  const renderCostPercentCell = (aggregates: RowAggregates): React.ReactNode =>
    aggregates.costPercent === null ? (
      <Dash tooltip="No income across the rows shown, so a cost % can't be calculated." />
    ) : (
      formatPercent(aggregates.costPercent)
    );

  const sortButtonProps = { activeSortKey: sortKey, sortDir, onSort: handleSort };

  // --- Row renderers — closures so the flat list and each group's rows can
  // both call the same rendering without prop-drilling every show* flag. ---

  const renderPaddockRow = (row: AnalysisRow, indent = false, idx = 0) => {
    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
    return (
    <tr key={row.inputId} className={`group hover:bg-rowHover ${rowBg}`}>
      <td
        className={`${columnWidthClass('paddockName')} py-1.5 pl-2 pr-6 text-base text-charcoal ${stickyCol0BodyClass(rowBg)} ${ROW_DIVIDER} ${stickyEdgeClass}`}
      >
        {/* block (not inline) so the padding-left indents every wrapped
            line, not just the first — inline elements only apply
            padding at the start/end of the whole run, not per line. */}
        <span className={indent ? 'block pl-8' : ''}>{row.paddockName}</span>
        <span className={`flex gap-3 ${indent ? 'pl-8' : ''}`}>
          <LinkButton
            className="text-xs"
            onClick={() =>
              navigateToFarmsView({
                nodeKey: `${INPUT_KEY_PREFIX}${row.inputId}`,
                tab: INPUT_DOC_TAB_PLANNING,
              })
            }
          >
            {LINK_OPEN_PLAN_LABEL}
          </LinkButton>
          {row.paddockId && (
            <LinkButton
              className="text-xs"
              onClick={() =>
                navigateToFarmsView({
                  nodeKey: `${PADDOCK_KEY_PREFIX}${row.paddockId}`,
                  tab: INPUT_DOC_TAB_DIAGNOSTICS,
                })
              }
            >
              {LINK_EFFICIENCY_TRENDS_LABEL}
            </LinkButton>
          )}
        </span>
      </td>
      {showFarmName && (
        <td
          className={`max-w-[12rem] py-1.5 pr-6 text-base text-charcoal ${ROW_DIVIDER}`}
        >
          {row.farmName}
        </td>
      )}
      {showAreaHa && (
        <td className={`${bodyCellClass} ${AREA_GUTTER}`}>
          {fmt2(row.areaHa)}
        </td>
      )}
      {showCrop && (
        <td
          className={`${columnWidthClass('crop')} py-1.5 pr-6 text-base text-charcoal ${ROW_DIVIDER}`}
        >
          {row.crop}
        </td>
      )}
      {showVariety && (
        <td
          className={`${columnWidthClass('variety')} py-1.5 pr-6 text-base text-charcoal ${ROW_DIVIDER}`}
        >
          {row.variety}
        </td>
      )}
      {showStartDateCol && (
        <td
          className={`max-w-[10rem] py-1.5 pr-6 text-base text-charcoal ${ROW_DIVIDER}`}
        >
          {formatStartDate(row.startDate)}
        </td>
      )}
      {showPriceCol && (
        <td className={bodyCellClass}>{formatCurrency(row.price)}</td>
      )}
      {showYieldCol && (
        <td className={bodyCellClass}>
          {fmt2(row.yield)}
          {yieldAmbiguous && <UnitCaption>{row.yieldUnit}/ha</UnitCaption>}
        </td>
      )}
      {showProductionCol && (
        <td className={bodyCellClass}>
          {fmt2(row.production)}
          {yieldAmbiguous && <UnitCaption>{row.yieldUnit}</UnitCaption>}
        </td>
      )}
      {showIncomeCol && (
        <td className={bodyCellClass}>
          {formatCurrency(showPerHa ? row.incomePerHa : row.incomeTotal)}
        </td>
      )}
      {visibleCostCategories.map((category) => (
        <td key={category} className={bodyCellClass}>
          {formatCurrency(
            (showPerHa ? row.costsPerHa : row.costsTotal)[category] ?? 0
          )}
        </td>
      ))}
      {showTotalCostsCol && (
        <td className={`${bodyCellClass} ${totalCostsGutterClass}`}>
          {formatCurrency(
            showPerHa ? row.totalCostsPerHa : row.totalCostsTotal
          )}
        </td>
      )}
      {showGrossMarginCol && (
        <td
          className={`w-24 px-2 py-1.5 text-right tabular-nums ${ROW_DIVIDER} ${
            (showPerHa ? row.grossMarginPerHa : row.grossMarginTotal) < 0
              ? 'text-danger'
              : 'text-charcoal'
          }`}
        >
          {formatCurrency(
            showPerHa ? row.grossMarginPerHa : row.grossMarginTotal
          )}
        </td>
      )}
      {showCostPercentCol && (
        <td className={bodyCellClass}>
          {row.costPercentIncome === null ? (
            <Dash tooltip="No income recorded for this paddock at the selected scenario, so cost % can't be calculated." />
          ) : (
            formatPercent(row.costPercentIncome)
          )}
        </td>
      )}
    </tr>
    );
  };

  const renderGroupHeaderRow = (group: { key: string; rows: AnalysisRow[] }) => {
    const isCollapsed = collapsedGroups.has(group.key);
    const aggregates = aggregateRowSet(
      group.rows,
      showPerHa,
      summaryMode,
      weightAverageByHa
    );
    return (
      <tr
        key={`group-${group.key}`}
        className={GROUP_HEADER_ROW}
        onClick={() => onToggleGroup(group.key)}
      >
        <td
          className={`${columnWidthClass('paddockName')} py-1.5 pl-2 pr-6 text-base font-semibold text-charcoal ${GROUP_HEADER_STICKY_BASE} ${GROUP_ROW_DIVIDER} ${stickyEdgeClass}`}
        >
          <span className="inline-flex items-center gap-1.5">
            {isCollapsed ? (
              <LuChevronRight size={14} aria-hidden="true" />
            ) : (
              <LuChevronDown size={14} aria-hidden="true" />
            )}
            {group.key}
            <span className="font-normal text-gray-muted">
              ({group.rows.length})
            </span>
          </span>
        </td>
        {showFarmName && <td className={GROUP_ROW_DIVIDER} />}
        {showAreaHa && (
          <td className={`${groupCellClass} ${AREA_GUTTER}`}>
            {fmt2(aggregates.area)}
          </td>
        )}
        {showCrop && (
          <td className={`${columnWidthClass('crop')} ${GROUP_ROW_DIVIDER}`} />
        )}
        {showVariety && (
          <td
            className={`${columnWidthClass('variety')} ${GROUP_ROW_DIVIDER}`}
          />
        )}
        {showStartDateCol && <td className={GROUP_ROW_DIVIDER} />}
        {showPriceCol && (
          <td className={groupCellClass}>{renderPriceCell(aggregates)}</td>
        )}
        {showYieldCol && (
          <td className={groupCellClass}>{renderYieldCell(aggregates)}</td>
        )}
        {showProductionCol && (
          <td className={groupCellClass}>
            {renderProductionCell(aggregates)}
          </td>
        )}
        {showIncomeCol && (
          <td className={groupCellClass}>
            {formatCurrency(aggregates.income)}
          </td>
        )}
        {visibleCostCategories.map((category) => (
          <td key={category} className={groupCellClass}>
            {formatCurrency(aggregates.costsByCategory[category] ?? 0)}
          </td>
        ))}
        {showTotalCostsCol && (
          <td className={`${groupCellClass} ${totalCostsGutterClass}`}>
            {formatCurrency(aggregates.totalCosts)}
          </td>
        )}
        {showGrossMarginCol && (
          <td
            className={`w-24 px-2 py-1.5 text-right tabular-nums font-semibold ${GROUP_ROW_DIVIDER} ${
              aggregates.grossMargin < 0 ? 'text-danger' : 'text-charcoal'
            }`}
          >
            {formatCurrency(aggregates.grossMargin)}
          </td>
        )}
        {showCostPercentCol && (
          <td className={groupCellClass}>
            {renderCostPercentCell(aggregates)}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="summary-grid-scroll w-0 min-w-full flex-1 overflow-x-auto"
    >
      {/* border-separate (not collapse) — collapsed borders on sticky cells
          render inconsistently across browsers/scroll positions since the
          collapse algorithm has to pick a winner between adjacent cells'
          borders; separate borders paint independently on each cell, so the
          footer's top border always shows regardless of scroll state. */}
      <table className="w-max border-separate border-spacing-0 text-base">
        <thead>
          <tr>
            <th
              className={`${columnWidthClass('paddockName')} relative pl-2 pr-6 text-left ${HEADER_BASE} z-20 left-0 ${stickyEdgeClass}`}
              aria-sort={getAriaSort('paddockName', sortKey, sortDir)}
            >
              <SortButton sortKeyId="paddockName" align="left" {...sortButtonProps}>
                Paddock
              </SortButton>
              <ResizeHandle
                widthIndex={columnWidths.paddockName}
                onResize={(i) => setColumnWidth('paddockName', i)}
              />
            </th>
            {showFarmName && (
              <th
                className={headerTextClass}
                aria-sort={getAriaSort('farmName', sortKey, sortDir)}
              >
                <SortButton sortKeyId="farmName" align="left" {...sortButtonProps}>
                  Farm Name
                </SortButton>
              </th>
            )}
            {showAreaHa && (
              <th
                className={`${headerCellClass} ${AREA_GUTTER}`}
                aria-sort={getAriaSort('areaHa', sortKey, sortDir)}
              >
                <SortButton sortKeyId="areaHa" align="right" {...sortButtonProps}>
                  Area
                  <UnitCaption>(ha)</UnitCaption>
                </SortButton>
              </th>
            )}
            {showCrop && (
              <th
                className={`${headerResizableTextClass} ${columnWidthClass('crop')}`}
                aria-sort={getAriaSort('crop', sortKey, sortDir)}
              >
                <SortButton sortKeyId="crop" align="left" {...sortButtonProps}>
                  Crop
                </SortButton>
                <ResizeHandle
                  widthIndex={columnWidths.crop}
                  onResize={(i) => setColumnWidth('crop', i)}
                />
              </th>
            )}
            {showVariety && (
              <th
                className={`${headerResizableTextClass} ${columnWidthClass('variety')}`}
                aria-sort={getAriaSort('variety', sortKey, sortDir)}
              >
                <SortButton sortKeyId="variety" align="left" {...sortButtonProps}>
                  Variety
                </SortButton>
                <ResizeHandle
                  widthIndex={columnWidths.variety}
                  onResize={(i) => setColumnWidth('variety', i)}
                />
              </th>
            )}
            {showStartDateCol && (
              <th
                className={headerTextClass}
                aria-sort={getAriaSort('startDate', sortKey, sortDir)}
              >
                <SortButton sortKeyId="startDate" align="left" {...sortButtonProps}>
                  Start Date
                </SortButton>
              </th>
            )}
            {showPriceCol && (
              <th
                className={headerCellClass}
                aria-sort={getAriaSort('price', sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId="price"
                  align="right"
                  tooltip={TOOLTIP_PRICE}
                  {...sortButtonProps}
                >
                  Price
                </SortButton>
              </th>
            )}
            {showYieldCol && (
              <th
                className={headerCellClass}
                aria-sort={getAriaSort('yield', sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId="yield"
                  align="right"
                  tooltip={yieldAmbiguous ? undefined : TOOLTIP_YIELD}
                  {...sortButtonProps}
                >
                  Yield
                  {!yieldAmbiguous && (
                    <UnitCaption>({uniformYieldUnit}/ha)</UnitCaption>
                  )}
                  {yieldAmbiguous && (
                    <HeaderInfo
                      tooltip={`Paddocks in this view record yield in different units (${distinctYieldUnitsLabel}) — shown per row instead of in the header.`}
                    />
                  )}
                </SortButton>
              </th>
            )}
            {showProductionCol && (
              <th
                className={headerCellClass}
                aria-sort={getAriaSort('production', sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId="production"
                  align="right"
                  tooltip={yieldAmbiguous ? undefined : TOOLTIP_PRODUCTION}
                  {...sortButtonProps}
                >
                  Prod.
                  {!yieldAmbiguous && (
                    <UnitCaption>({uniformYieldUnit})</UnitCaption>
                  )}
                  {yieldAmbiguous && (
                    <HeaderInfo
                      tooltip={`Paddocks in this view record yield in different units (${distinctYieldUnitsLabel}) — shown per row instead of in the header.`}
                    />
                  )}
                </SortButton>
              </th>
            )}
            {showIncomeCol && (
              <th
                className={headerCellClass}
                aria-sort={getAriaSort('income', sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId="income"
                  align="right"
                  tooltip={TOOLTIP_INCOME}
                  {...sortButtonProps}
                >
                  Income
                </SortButton>
              </th>
            )}
            {visibleCostCategories.map((category) => (
              <th
                key={category}
                className={headerCellClass}
                aria-sort={getAriaSort(category, sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId={category}
                  align="right"
                  tooltip={costCategoryTooltip(SUMMARY_ENTITY_LABELS[category])}
                  {...sortButtonProps}
                >
                  {SUMMARY_ENTITY_LABELS[category]}
                </SortButton>
              </th>
            ))}
            {showTotalCostsCol && (
              <th
                className={`${headerCellClass} ${totalCostsGutterClass}`}
                aria-sort={getAriaSort('totalCosts', sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId="totalCosts"
                  align="right"
                  tooltip={TOOLTIP_TOTAL_COSTS}
                  {...sortButtonProps}
                >
                  Tot. Costs
                </SortButton>
              </th>
            )}
            {showGrossMarginCol && (
              <th
                className={headerCellClass}
                aria-sort={getAriaSort('grossMargin', sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId="grossMargin"
                  align="right"
                  tooltip={TOOLTIP_GROSS_MARGIN}
                  {...sortButtonProps}
                >
                  Gross Margin
                </SortButton>
              </th>
            )}
            {showCostPercentCol && (
              <th
                className={headerCellClass}
                aria-sort={getAriaSort('costPercentIncome', sortKey, sortDir)}
              >
                <SortButton
                  sortKeyId="costPercentIncome"
                  align="right"
                  tooltip={TOOLTIP_COST_PERCENT}
                  {...sortButtonProps}
                >
                  Cost % Income
                </SortButton>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {groupBy === 'none'
            ? sortedRows.map((row, idx) => renderPaddockRow(row, false, idx))
            : groups!.flatMap((group) => [
                renderGroupHeaderRow(group),
                ...(collapsedGroups.has(group.key)
                  ? []
                  : group.rows.map((row, idx) =>
                      renderPaddockRow(row, true, idx)
                    )),
              ])}
        </tbody>
        <tfoot>
          <tr>
            <td
              className={`${columnWidthClass('paddockName')} pl-2 pr-6 text-right ${FOOTER_BASE} z-20 left-0 ${stickyEdgeClass}`}
            >
              {isAverage ? 'Average:' : 'Total:'}
            </td>
            {showFarmName && <td className={FOOTER_BASE} />}
            {showAreaHa && (
              <td className={`${footerCellClass} ${AREA_GUTTER}`}>
                {fmt2(totals.area)}
              </td>
            )}
            {showCrop && (
              <td className={`${columnWidthClass('crop')} ${FOOTER_BASE}`} />
            )}
            {showVariety && (
              <td className={`${columnWidthClass('variety')} ${FOOTER_BASE}`} />
            )}
            {showStartDateCol && <td className={FOOTER_BASE} />}
            {showPriceCol && (
              <td className={footerCellClass}>{renderPriceCell(totals)}</td>
            )}
            {showYieldCol && (
              <td className={footerCellClass}>{renderYieldCell(totals)}</td>
            )}
            {showProductionCol && (
              <td className={footerCellClass}>
                {renderProductionCell(totals)}
              </td>
            )}
            {showIncomeCol && (
              <td className={footerCellClass}>
                {formatCurrency(totals.income)}
              </td>
            )}
            {visibleCostCategories.map((category) => (
              <td key={category} className={footerCellClass}>
                {formatCurrency(totals.costsByCategory[category] ?? 0)}
              </td>
            ))}
            {showTotalCostsCol && (
              <td className={`${footerCellClass} ${totalCostsGutterClass}`}>
                {formatCurrency(totals.totalCosts)}
              </td>
            )}
            {showGrossMarginCol && (
              <td
                className={`w-24 px-2 text-right ${FOOTER_BASE} z-10 ${
                  totals.grossMargin < 0 ? 'text-danger' : 'text-charcoal'
                }`}
              >
                {formatCurrency(totals.grossMargin)}
              </td>
            )}
            {showCostPercentCol && (
              <td className={footerCellClass}>
                {renderCostPercentCell(totals)}
              </td>
            )}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default AnalysisGrid;
